import { useEffect, useRef, useState } from 'react';
import { UpgradeMessageEventKeys } from 'ui-addon-upgrades/_core/common/constants/upgradeMessageEventKeys';
import { track } from '../_core/common/eventTracking/tracker';
import { useUpgradePoint } from './useUpgradePoint';
import { getUpgradeConfigTrackingProperties, getUpgradePointPromise, updateViewedUpgradePointsCache } from './utils';
import { logRavenException } from '../_core/utils/errors';
const handleFetchUpgradePoint = (upgradeKey, isFeatureLocked) => {
  const upgradePointPromise = getUpgradePointPromise({
    upgradeKey,
    includePurchaseMotions: false
  });
  upgradePointPromise.then(upgradePoint => {
    if (isFeatureLocked && upgradePoint) {
      updateViewedUpgradePointsCache(upgradeKey, () => {
        track('interaction', Object.assign({
          action: 'viewed upgrade point'
        }, getUpgradeConfigTrackingProperties(upgradePoint)));
      });
    }
  }).catch(error => {
    logRavenException({
      customMessage: 'Error fetching upgrade point in useUpgradeEventListener',
      error,
      uniqueId: upgradeKey,
      fingerprint: ['handleFetchUpgradePoint']
    });
  });
};

/**
 * `useUpgradeEventListener` should be called in the top window of your
 * application when using `useUpgradeModal` inside of an iframe. This will
 * mount an event listener to listen for upgrade events, which will in turn
 * open the upgrade modal in the parent window instead of the iframe.
 *
 * @example
 * ```tsx
 * const WrapperComponent = () => {
 *   useUpgradeEventListener();
 *
 *   return <iframe src="your-source.hubspot.com" />;
 * }
 * ```
 */
export const useUpgradeEventListener = () => {
  // We store this item in state as an object in order
  // to re-evaluate this component on every message.
  // This is because when the modal key changes,
  // we need `useUpgradePoint` to re-run so that
  // `openUpgradePage` opens for the right upgrade point.
  const [key, setUpgradeKey] = useState({
    inner: ''
  });
  const {
    openUpgradePage
  } = useUpgradePoint(key.inner);
  const openUpgradePageRef = useRef(openUpgradePage);
  useEffect(() => {
    openUpgradePageRef.current = openUpgradePage;
  }, [openUpgradePage]);
  useEffect(() => {
    const handleMessage = messageEvent => {
      if (!messageEvent || !messageEvent.data) {
        return;
      }
      switch (messageEvent.data.event) {
        case UpgradeMessageEventKeys.OPEN_UPGRADE_PAGE:
          setUpgradeKey({
            inner: messageEvent.data.upgradeKey
          });
          break;
        case UpgradeMessageEventKeys.FETCH_UPGRADE_POINT:
          handleFetchUpgradePoint(messageEvent.data.upgradeKey, messageEvent.data.isFeatureLocked);
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  useEffect(() => {
    if (key.inner) {
      openUpgradePageRef.current();
    }
  }, [key]);
};